import { useState } from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useAccess } from "hooks/useAccess";
import {
  Drawer,
  List,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Toolbar,
  Box,
  Tooltip,
} from "@mui/material";
import {
  People as PeopleIcon,
  Menu as MenuIcon,
  TransitEnterexit as TransitEnterexitIcon,
  Summarize as SummarizeIcon,
  NoteAdd as NoteAddIcon,
  Assessment,
  Business as BusinessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccountCircle as AccountCircleIcon,
  AssignmentInd as AssignmentIndIcon,
} from "@mui/icons-material";
import { Avatar, Typography as MuiTypography } from "@mui/material";
import useDialog from "hooks/useDialog";

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_MD = 210;
const MINI_WIDTH = 56;

function SideBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const location = useLocation();
  const history = useHistory();
  const { promptConfirm } = useDialog();
  const { logout } = useAuth();
  const { user: currentUser } = useAuth();
  const { isMonitoring, isHead, isUniversal, isCgs, canSeeAllApps, canSubmitApp, canManageOrgs, canManageUsers, activeAssignmentTypes } = useAccess();
  const initials = currentUser?.name?.split(" ").slice(0, 2).map((n) => n[0] || "").join("").toUpperCase() || "";

  const rawMenuItems = [
    { name: "უნივერსალური",  link: "/monitoring/universal", icon: <SummarizeIcon />, visible: false },
    { name: "მიკრო (CGS)",  link: "/monitoring/cgs",       icon: <SummarizeIcon />, visible: isCgs || canSeeAllApps },

    { name: "რეპორტი",               link: "/report",               icon: <Assessment />,    visible: canSeeAllApps || (isMonitoring && isHead && activeAssignmentTypes.length > 0) },
  ];

  const rawSubMenuItems = [
    { name: "მომხმარებლები", link: "/users",         icon: <PeopleIcon />,        visible: canManageUsers },
    { name: "კომპანიები",   link: "/organizations", icon: <BusinessIcon />,      visible: canManageOrgs },
    { name: "მონიტორინგები", link: "/assignments",   icon: <AssignmentIndIcon />, visible: canSeeAllApps },
    {
      name: currentUser?.name || "პროფილი",
      link: "/profile",
      icon: initials
        ? <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: "rgba(255,255,255,0.25)" }}>{initials}</Avatar>
        : <AccountCircleIcon />,
      visible: true,
    },
  ];

  const menuItems    = rawMenuItems.filter((i) => i.visible);
  const subMenuItems = rawSubMenuItems.filter((i) => i.visible);

  async function handleLogout() {
    const confirmed = await promptConfirm({
      title: "გასვლა",
      message: "გსურთ სისტემიდან გასვლა?",
    });
    if (!confirmed) { history.push("/"); return; }
    await logout();
    history.push("/");
  }

  const renderItem = (item, index) => (
    <ListItem key={index} disablePadding sx={{ display: "block" }}>
      <Tooltip title={desktopOpen ? "" : item.name} placement="right" arrow>
        <ListItemButton
          component={Link}
          to={item.link}
          selected={location.pathname === item.link}
          sx={{
            minHeight: 48,
            justifyContent: desktopOpen ? "initial" : "center",
            px: 2.5,
            "&.Mui-selected": {
              backgroundColor: "rgba(255,255,255,0.08)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
            },
            "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
          }}
        >
          <ListItemIcon
            sx={{
              color: "#e0e0e0",
              minWidth: 0,
              mr: desktopOpen ? 2 : "auto",
              justifyContent: "center",
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.name}
            primaryTypographyProps={{ sx: { fontSize: "0.8125rem" } }}
            sx={{
              opacity: desktopOpen ? 1 : 0,
              transition: "opacity 0.2s",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  const desktopDrawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Toggle button row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: desktopOpen ? "flex-end" : "center",
          py: 1,
          px: 0.5,
          minHeight: 48,
        }}
      >
        <IconButton onClick={() => setDesktopOpen(!desktopOpen)} sx={{ color: "#e0e0e0" }}>
          {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>

      {/* Logo — only visible when expanded */}
      {desktopOpen && (
        <Box
          component="img"
          src="/logo.png"
          alt="logo"
          sx={{ width: "80%", margin: "0 auto 16px auto", display: "block" }}
        />
      )}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

      <List sx={{ color: "#e0e0e0", flex: 1 }}>
        {menuItems.map(renderItem)}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

      <List sx={{ color: "#e0e0e0" }}>
        {subMenuItems.map(renderItem)}
        <ListItem disablePadding sx={{ display: "block" }}>
          <Tooltip title={desktopOpen ? "" : "სისტემიდან გასვლა"} placement="right" arrow>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                minHeight: 48,
                justifyContent: desktopOpen ? "initial" : "center",
                px: 2.5,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
              }}
            >
              <ListItemIcon
                sx={{
                  color: "#e0e0e0",
                  minWidth: 0,
                  mr: desktopOpen ? 2 : "auto",
                  justifyContent: "center",
                }}
              >
                <TransitEnterexitIcon />
              </ListItemIcon>
              <ListItemText
                primary="სისტემიდან გასვლა"
                primaryTypographyProps={{ sx: { fontSize: "0.8125rem" } }}
                sx={{
                  opacity: desktopOpen ? 1 : 0,
                  transition: "opacity 0.2s",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </Box>
  );

  const mobileDrawerContent = (
    <Box>
      <Box
        component="img"
        src="/logo.png"
        alt="logo"
        sx={{ width: "80%", margin: "30px auto 0 auto", display: "block" }}
      />
      <List sx={{ color: "#e0e0e0", mt: 1 }}>
        {menuItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              component={Link}
              to={item.link}
              selected={location.pathname === item.link}
              onClick={() => setMobileOpen(false)}
              sx={{
                "&.Mui-selected": { backgroundColor: "rgba(255,255,255,0.08)" },
                "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
              }}
            >
              <ListItemIcon sx={{ color: "#e0e0e0" }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.name} primaryTypographyProps={{ sx: { fontSize: "0.8125rem" } }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
      <List sx={{ color: "#e0e0e0" }}>
        {subMenuItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              component={Link}
              to={item.link}
              selected={location.pathname === item.link}
              onClick={() => setMobileOpen(false)}
              sx={{
                "&.Mui-selected": { backgroundColor: "rgba(255,255,255,0.08)" },
                "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
              }}
            >
              <ListItemIcon sx={{ color: "#e0e0e0" }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.name} primaryTypographyProps={{ sx: { fontSize: "0.8125rem" } }} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon sx={{ color: "#e0e0e0" }}><TransitEnterexitIcon /></ListItemIcon>
            <ListItemText primary="სისტემიდან გასვლა" primaryTypographyProps={{ sx: { fontSize: "0.8125rem" } }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const currentDesktopWidth = desktopOpen ? DRAWER_WIDTH_MD : MINI_WIDTH;

  return (
    <>
      {/* Mobile AppBar — only on small screens */}
      <AppBar
        position="fixed"
        sx={{ display: { md: "none" }, width: "100%", zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: currentDesktopWidth },
          flexShrink: { md: 0 },
          transition: "width 0.2s ease",
          "@media print": { display: "none" },
        }}
      >
        {/* Mobile temporary drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              backgroundColor: "#2c5b94",
              boxSizing: "border-box",
            },
            "@media print": { display: "none !important" },
          }}
        >
          {mobileDrawerContent}
        </Drawer>

        {/* Desktop permanent mini/full drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: currentDesktopWidth,
              backgroundColor: "#2c5b94",
              boxSizing: "border-box",
              overflowX: "hidden",
              transition: "width 0.2s ease",
            },
            "@media print": { display: "none !important" },
          }}
          open
        >
          {desktopDrawerContent}
        </Drawer>
      </Box>
    </>
  );
}

export default SideBar;
